import { Flex, Skeleton, Text } from "@chakra-ui/react"
import { DataSource, Graph, TimeRange, Timeseries } from "@hydrologiq/schemas/1.0.0/installationPage"
import { ApolloError, useLazyQuery, useQuery } from "@apollo/client"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { RangeResolution, rangeFilter, rangeInMS } from "./rangeFilter"
import { IANATimeZone, nowDateTime, toTimestamp } from "@hydrologiq/common/helpers/date"
import TimeSeriesGraphImpl from "./TimeSeriesGraphImpl"
import { DateTime, milliseconds } from "timezonecomplete"
import { useEffect, useState, useMemo } from "react"
import { getQuery, getQueryRaw } from "@hydrologiq/shared/monitoring/graphing/queries"

interface TimeSeriesGraphProps {
  graph: Graph
  timeseries: Timeseries
  sourceRange: TimeRange
  installationId?: string
  installationEndDate: DateTime
  installationStartDate: DateTime
  assetId?: string
}

type TimeSeriesGraphImplProps = {
  graph: Graph
  timeseries: Timeseries
  range: string
  installationId?: string
  installationStartDate: DateTime
  endDate: DateTime
  start: DateTime
  stop: DateTime
  assetId?: string
}

type TimeSeriesGraphOptimisedProps = {} & TimeSeriesGraphImplProps

function TimeSeriesGraphOptimised({
  graph,
  timeseries,
  range,
  installationId,
  installationStartDate,
  endDate,
  start,
  stop,
  assetId,
}: TimeSeriesGraphOptimisedProps) {
  const vars: Record<string, any> = {
    field: graph.source.field,
    start: toTimestamp(start),
    stop: toTimestamp(stop),
    id: assetId || installationId,
    installation: installationId ?? "",
    installations: installationId ?? "",
    window: range,
    installationStart: toTimestamp(installationStartDate),
    location: IANATimeZone(),
    granularity: range,
  }

  let baseVars = { ...vars }

  if (graph.source.downsampled) {
    baseVars["downsampled"] = true
  }

  switch (graph.source.type) {
    case "InfluXDB":
      if (graph.source.influx?.bucket) baseVars["bucket"] = graph.source.influx.bucket
      if (graph.source.influx?.measurement) baseVars["measurement"] = graph.source.influx.measurement
      break
  }

  const sourceComparison = graph.sourceComparison as DataSource
  let fields: Record<string, { field: string; agg: string }> = {
    source: { field: graph.source.field, agg: graph.source.method ?? "mean" },
  }
  if (sourceComparison) {
    fields["comparison"] = { field: sourceComparison.field, agg: sourceComparison.method ?? "mean" }
  }
  baseVars["fields"] = fields

  const {
    loading,
    error,
    data,
  }: {
    loading: boolean
    error?: ApolloError | undefined
    data: gqlTypes.GetAssetMonitoringMultiFieldTimeseriesQuery | undefined
  } = useQuery(gqlTypes.GetAssetMonitoringMultiFieldTimeseriesDocument, {
    // @ts-ignore
    variables: baseVars,
  })
  const dataComparison =
    (data?.values?.filter((value) => value?.field === "comparison") as gqlTypes.TimeSeriesData[]) ?? []
  const dataSource = (data?.values?.filter((value) => value?.field === "source") as gqlTypes.TimeSeriesData[]) ?? []

  return loading ? (
    <Flex height="530px">
      <Skeleton height="100%" width="100%" />
    </Flex>
  ) : error ? (
    <Text>Error: {error.message}</Text>
  ) : !data ? (
    <Text>Error: invalid data format</Text>
  ) : (
    <TimeSeriesGraphImpl
      data={{ values: dataSource }}
      endDate={endDate}
      graph={graph}
      timeseries={timeseries}
      comparison={
        dataComparison && graph.sourceComparison && sourceComparison.label
          ? { label: sourceComparison.label, data: dataComparison }
          : undefined
      }
    />
  )
}

type TimeSeriesGraphRawProps = {} & TimeSeriesGraphImplProps

function TimeSeriesGraphRaw({
  graph,
  timeseries,
  range,
  installationId,
  installationStartDate,
  endDate,
  start,
  stop,
}: TimeSeriesGraphRawProps) {
  const vars: Record<string, any> = {
    field: graph.source.field,
    start: toTimestamp(start),
    stop: toTimestamp(stop),
    id: installationId,
    installation: installationId,
    installations: installationId,
    window: range,
    installationStart: toTimestamp(installationStartDate),
    location: IANATimeZone(),
  }

  let baseVars = { ...vars }

  switch (graph.source.type) {
    case "InfluXDB":
      if (graph.source.influx?.bucket) baseVars["bucket"] = graph.source.influx.bucket
      if (graph.source.influx?.measurement) baseVars["measurement"] = graph.source.influx.measurement
      break
  }

  const {
    loading,
    error,
    data,
  }: {
    loading: boolean
    error?: ApolloError | undefined
    data:
      | gqlTypes.InstallationDataWindowedQuery
      | gqlTypes.InstallationDataWindowedCombinedQuery
      | gqlTypes.InstallationHydrogenWindowedMwQuery
      | undefined
  } = useQuery(getQueryRaw(graph.toggles, graph.source.field, graph.source.method), {
    // @ts-ignore
    variables: baseVars,
  })

  const [dataComparison, setDataComparison] = useState<gqlTypes.TimeSeriesData[] | undefined>(undefined)

  const [getInstallationData, { loading: loadingAdditional }] = useLazyQuery(gqlTypes.InstallationDataWindowedDocument)
  const sourceComparison = graph.sourceComparison as DataSource
  useEffect(() => {
    if (sourceComparison) {
      if (!sourceComparison || !sourceComparison.field) return
      let varsAdditional = { ...baseVars }
      switch (sourceComparison.type) {
        case "InfluXDB":
          if (sourceComparison.influx?.bucket) varsAdditional["bucket"] = sourceComparison.influx.bucket
          if (sourceComparison.influx?.measurement) varsAdditional["measurement"] = sourceComparison.influx.measurement
          break
      }
      getInstallationData({
        // @ts-ignore
        variables: { ...varsAdditional, field: sourceComparison.field },
      }).then(({ data }) => {
        if (data && data.values) setDataComparison(data.values as gqlTypes.TimeSeriesData[])
      })
    }
  }, [graph.sourceComparison])

  return loadingAdditional || loading ? (
    <Flex height="530px">
      <Skeleton height="100%" width="100%" />
    </Flex>
  ) : error ? (
    <Text>Error: {error.message}</Text>
  ) : !data ? (
    <Text>Error: invalid data format</Text>
  ) : (
    <TimeSeriesGraphImpl
      data={data}
      endDate={endDate}
      graph={graph}
      timeseries={timeseries}
      comparison={
        dataComparison && graph.sourceComparison && sourceComparison.label
          ? { label: sourceComparison.label, data: dataComparison }
          : undefined
      }
    />
  )
}

function TimeSeriesGraphDownSampled({
  graph,
  timeseries,
  range,
  installationId,
  installationStartDate,
  endDate,
  start,
  stop,
}: TimeSeriesGraphRawProps) {
  const baseVars: any = {
    start: toTimestamp(start),
    stop: toTimestamp(stop),
    id: installationId ?? "",
    installation: installationId ?? "",
    installations: installationId ?? "",
    granularity: range,
    location: IANATimeZone(),
  }
  let vars = { ...baseVars }
  if (graph.source.field == "weight") vars["installationStart"] = toTimestamp(installationStartDate)
  if (graph.source.field_downsampled) {
    vars["field"] = graph.source.field_downsampled
    vars["bucket"] = "installations_5m"
    vars["measurement"] = "summary"
  }

  const {
    loading,
    error,
    data,
  }: {
    loading: boolean
    error?: ApolloError | undefined
    data:
      | gqlTypes.InstallationPowerAverageQuery
      | gqlTypes.InstallationPowerCombinedQuery
      | gqlTypes.InstallationWeightCombinedQuery
      | undefined
  } = useQuery(getQuery(graph.toggles, graph.source.field, graph.source.method), {
    variables: vars,
  })

  const [dataComparison, setDataComparison] = useState<gqlTypes.TimeSeriesData[] | undefined>(undefined)

  const [getInstallationData, { loading: loadingAdditional }] = useLazyQuery(gqlTypes.InstallationDataWindowedDocument)
  const sourceComparison = graph.sourceComparison as DataSource
  useEffect(() => {
    if (sourceComparison) {
      if (!sourceComparison || !sourceComparison.field) return
      let varsAdditional = { ...baseVars }
      switch (sourceComparison.type) {
        case "InfluXDB":
          if (sourceComparison.influx?.bucket) varsAdditional["bucket"] = sourceComparison.influx.bucket
          if (sourceComparison.influx?.measurement) varsAdditional["measurement"] = sourceComparison.influx.measurement
          break
      }
      getInstallationData({
        // @ts-ignore
        variables: { ...varsAdditional, field: sourceComparison.field },
      }).then(({ data }) => {
        if (data && data.values) setDataComparison(data.values as gqlTypes.TimeSeriesData[])
      })
    }
  }, [graph.sourceComparison])

  return loading || loadingAdditional ? (
    <Flex height="530px">
      <Skeleton height="100%" width="100%" />
    </Flex>
  ) : error ? (
    <Text>Error: {error.message}</Text>
  ) : !data ? (
    <Text>Error: invalid data format</Text>
  ) : (
    <TimeSeriesGraphImpl
      data={data}
      endDate={endDate}
      graph={graph}
      timeseries={timeseries}
      comparison={
        dataComparison && graph.sourceComparison && sourceComparison.label
          ? { label: sourceComparison.label, data: dataComparison }
          : undefined
      }
    />
  )
}

function TimeSeriesGraph({
  graph,
  timeseries,
  sourceRange,
  installationId,
  installationEndDate,
  installationStartDate,
  assetId,
}: TimeSeriesGraphProps) {
  const endDate = useMemo(() => {
    const dateNow = nowDateTime()
    return dateNow.unixUtcMillis() > installationEndDate.unixUtcMillis() ? installationEndDate : dateNow
  }, [installationEndDate])

  const rangeMS = rangeInMS(sourceRange.quantity, sourceRange.resolution as RangeResolution)

  const stop = useMemo(() => endDate, [endDate])
  const start = useMemo(() => endDate.clone().sub(milliseconds(rangeMS)), [endDate, rangeMS])
  const range = useMemo(() => timeseries.interval
    ? rangeFilter(timeseries.interval.quantity, timeseries.interval.resolution as RangeResolution)
    : "30s", [timeseries.interval])

    const windowMS = timeseries.interval
    ? rangeInMS(timeseries.interval.quantity, timeseries.interval.resolution as RangeResolution)
    : rangeInMS(30, "second")
  const downsampled = windowMS >= 5 * 60000 && ["weight", "power"].includes(graph.source.field)

  const GraphImpl =
    (graph.isOptimised || !installationId) && assetId
      ? TimeSeriesGraphOptimised
      : downsampled
      ? TimeSeriesGraphDownSampled
      : TimeSeriesGraphRaw

  return (
    <GraphImpl
      endDate={endDate}
      graph={graph}
      installationId={installationId}
      installationStartDate={installationStartDate}
      range={range}
      start={start}
      stop={stop}
      timeseries={timeseries}
      assetId={assetId}
    />
  )
}

export default TimeSeriesGraph
